import { Img } from "@/components/site/Img";
import { useState } from "react";
import { Wrench, Droplets, Sparkles, Paintbrush, Cog, Snowflake, ArrowRight, MessageCircle, Star, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { SMS_DRAFTS, PHONE_HREF, PHONE_DISPLAY } from "@/lib/contact";
import { useSmsHref } from "@/components/site/ContactButtons";
import { ReserveModal } from "@/components/site/ReserveModal";
import { repairPhotos } from "@/components/site/Repairs";
import { track } from "@/lib/track";

const SERVICES = [
  { icon: Wrench, label: "Fountain repairs" },
  { icon: Droplets, label: "Cleaning" },
  { icon: Cog, label: "Routine maintenance" },
  { icon: Paintbrush, label: "Restoration & refinishing" },
  { icon: Sparkles, label: "Pump problems" },
  { icon: Snowflake, label: "Winterizing" },
];

const processPhotos = [repairPhotos[0], repairPhotos[3], repairPhotos[6], repairPhotos[7]];

const BeforeAfter = () => (
  <div className="grid grid-cols-2 gap-3 md:gap-4">
    <figure className="relative overflow-hidden aspect-[3/4] bg-[#1F1E1C]">
      <Img
        src="/images/cleaning-before.jpg"
        alt="Algae-covered concrete fountain before a professional cleaning in Georgia"
        loading="lazy"
        className="w-full h-full object-cover"
        sizes="(min-width: 1024px) 25vw, 50vw"
      />
      <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#1F1E1C]/85 text-[#F9F6F0] text-[10px] tracking-[0.22em] uppercase">
        Before cleaning &middot; example
      </span>
    </figure>
    <figure className="relative overflow-hidden aspect-[3/4] bg-[#1F1E1C] border-2 border-[#C8B387]">
      <Img
        src="/images/cleaning-after.jpg"
        alt="A concrete fountain clean and bright after one of Chris's cleaning visits"
        loading="lazy"
        className="w-full h-full object-cover"
        sizes="(min-width: 1024px) 25vw, 50vw"
      />
      <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#C8B387] text-[#1F1E1C] text-[10px] tracking-[0.22em] uppercase">
        After cleaning &middot; separate project
      </span>
    </figure>
    <figcaption className="col-span-2 text-center text-[11px] tracking-[0.18em] uppercase text-[#9A958B]">
      Examples from two separate cleaning visits.
    </figcaption>
  </div>
);

const ProcessStrip = () => (
  <div data-testid="repairs-process-strip" className="mt-10 md:mt-14 border-t border-white/10 pt-8 md:pt-10">
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
      <p className="text-[#C9C4BA] text-sm md:text-base leading-relaxed max-w-xl">
        Cracked bowls, failed pumps, worn finishes: most older fountains can be
        brought back rather than replaced. A full restoration, start to finish.
      </p>
      <Link
        to="/fountain-repair"
        data-testid="seasonal-services-repair-link"
        className="inline-flex items-center gap-2 text-[#C8B387] hover:text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase whitespace-nowrap transition-colors"
      >
        Repair &amp; restoration details
        <ArrowRight size={13} strokeWidth={1.5} />
      </Link>
    </div>
    <div className="grid grid-cols-4 gap-2 md:gap-4">
      {processPhotos.map((p, i) => (
        <figure key={p.src} data-testid={`repairs-photo-${i + 1}`}>
          <div className="w-full aspect-square md:aspect-[4/5] overflow-hidden bg-[#1F1E1C]">
            <Img src={p.src} alt={p.alt} className="w-full h-full object-cover" loading="lazy" sizes="(min-width: 1024px) 15vw, 25vw" />
          </div>
          <figcaption className="hidden sm:block mt-2 text-center uppercase text-[10px] tracking-[0.18em] text-[#9A958B]">
            {p.caption}
          </figcaption>
        </figure>
      ))}
    </div>
  </div>
);

export default function SeasonalServices() {
  const serviceSms = useSmsHref(SMS_DRAFTS.service);
  const [showMaintenance, setShowMaintenance] = useState(false);
  return (
    <section
      id="seasonal-services"
      data-testid="seasonal-services-section"
      className="bg-[#26241F] text-[#F9F6F0] border-b border-black/20"
    >
      <span id="repairs" className="block relative -top-24" aria-hidden />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8B387] mb-3">
              Winter is coming
            </div>
            <h2
              data-testid="seasonal-services-heading"
              className="font-serif font-light text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.1] tracking-tight mb-4"
            >
              Repairs, cleaning, and winterizing. Keep it running beautifully.
            </h2>
            <p className="text-[#C9C4BA] text-sm md:text-base leading-relaxed mb-6 max-w-xl">
              This is the season to fix, clean, and protect what you already
              have. Bought a fountain somewhere else?{" "}
              <Link
                to="/fountain-repair#installation"
                data-testid="seasonal-installation-link"
                className="text-[#C8B387] underline underline-offset-2 hover:text-[#F9F6F0] transition-colors"
              >
                Chris installs those too.
              </Link>{" "}
              He still casts new pieces all winter.
            </p>

            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-7 max-w-lg" data-testid="seasonal-services-list">
              {SERVICES.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 text-sm text-[#EDE9DF]">
                  <Icon size={14} strokeWidth={1.5} className="text-[#C8B387] flex-shrink-0" />
                  {label}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <a
                href={PHONE_HREF}
                onClick={() => track("call_click", "Seasonal services")}
                data-testid="seasonal-services-call"
                aria-label={`Call Chris at ${PHONE_DISPLAY}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#C8B387] text-[#1F1E1C] text-[11px] tracking-[0.18em] uppercase hover:bg-[#F3EDDF] transition-colors text-center sm:whitespace-nowrap"
              >
                <Phone size={14} strokeWidth={1.5} className="flex-shrink-0" />
                Call Chris &mdash; {PHONE_DISPLAY}
              </a>
              <a
                href={serviceSms}
                onClick={() => track("text_click", "Seasonal services estimate")}
                data-testid="seasonal-services-estimate"
                aria-label={`Text Chris at ${PHONE_DISPLAY} or send a photo`}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#C8B387] text-[#C8B387] text-[11px] tracking-[0.18em] uppercase hover:bg-[#C8B387] hover:text-[#1F1E1C] transition-colors text-center sm:whitespace-nowrap"
              >
                <MessageCircle size={14} strokeWidth={1.5} className="flex-shrink-0" />
                Text Chris / Send a Photo
              </a>
            </div>
            <button
              type="button"
              onClick={() => setShowMaintenance(true)}
              data-testid="request-maintenance-button"
              className="inline-flex items-center gap-2 mt-4 min-h-[44px] text-[#C8B387] hover:text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase transition-colors"
            >
              <Wrench size={12} strokeWidth={1.5} className="flex-shrink-0" />
              Or request maintenance by form
              <ArrowRight size={13} strokeWidth={1.5} />
            </button>

            <a
              href="#reviews"
              onClick={() => track("shop_click", "Seasonal services reviews")}
              data-testid="seasonal-services-reviews"
              className="inline-flex items-center gap-2 mt-5 text-[#C8B387] hover:text-[#C8B387] text-[11px] tracking-[0.22em] uppercase transition-colors"
            >
              <Star size={12} strokeWidth={1.5} fill="currentColor" />
              Read our 5-star reviews
              <ArrowRight size={13} strokeWidth={1.5} />
            </a>
          </div>

          <BeforeAfter />
        </div>

        <ProcessStrip />

        <div
          data-testid="seasonal-secondary-paths"
          className="mt-8 md:mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 text-[11px] tracking-[0.22em] uppercase"
        >
          <span className="text-[#9A958B]">Not here for a repair?</span>
          <Link
            to="/available-now"
            data-testid="seasonal-browse-link"
            className="inline-flex items-center gap-2 text-[#C8B387] hover:text-[#F9F6F0] transition-colors"
          >
            Browse Available Pieces
            <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
          <a
            href="#contact"
            data-testid="seasonal-custom-link"
            className="inline-flex items-center gap-2 text-[#C8B387] hover:text-[#F9F6F0] transition-colors"
          >
            Plan a Custom Fountain
            <ArrowRight size={13} strokeWidth={1.5} />
          </a>
        </div>
      </div>
      <ReserveModal
        open={showMaintenance}
        onClose={() => setShowMaintenance(false)}
        piece="Fountain or statue maintenance"
        mode="maintenance"
      />
    </section>
  );
}
